import { shouldTableSupportFlag } from './shouldTableSupportFlag';
import shouldShowFolderTag from '../selectors/shouldShowFolderTag';
import { isGroupTableQuery } from 'owa-group-utils';
import { isSingleLineListView } from 'owa-mail-layout';
import type { MailListTableProps } from 'owa-mail-list-item-shared';
import { shouldTableShowCirclePersonas } from 'owa-mail-list-item-shared';
import { shouldTableSortByRenewTime } from 'owa-mail-triage-common';
import { isDumpsterSearchTable } from 'owa-mail-list-search';
import {
    isDumpsterTable,
    getStore as getListViewStore,
    isCopilotResultsTable,
    isPriorityView,
    isNeedsReplyView,
    type TableQueryType,
    type MailFolderTableQuery,
} from 'owa-mail-list-store';
import { getStore as getMailSearchStore } from 'owa-mail-search/lib/store/store';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import type { PrimaryMailboxSearchScope } from 'owa-search-service/lib/data/schema/SearchScope';
import { isCapabilityEnabled } from 'owa-capabilities';
import { mailboxModificationCapability } from 'owa-capabilities-definitions/lib/mailboxModificationCapability';
import { pinItemCapability } from 'owa-capabilities-definitions/lib/pinItemCapability';
import getMailboxInfo from 'owa-mail-mailboxinfo/lib/getMailboxInfo';
import type ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { logUsage } from 'owa-analytics';
import { isAccountFeatureEnabled, isGlobalFeatureEnabled } from 'owa-feature-flags';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';
import { getShowPreviewLinesInListView } from 'owa-messagelist-layout-option';
import { isPrioritizeOnDeliveryOn } from 'owa-copilot-settings-utils';

export default function getMailListTableProps(tableViewId: string): MailListTableProps {
    const tableView = getListViewStore().tableViews.get(tableViewId);
    const mailboxInfo = getMailboxInfo(tableView);
    const isUnifiedInbox = isUnifiedInboxMailboxInfo(mailboxInfo);

    // Unified Inbox is not modifiable for now so any new modification
    // that we start supporting, we should do an additional check for isUnifiedInbox
    const isMailboxModifiable = isCapabilityEnabled(mailboxModificationCapability, mailboxInfo);
    const tableQuery = tableView?.tableQuery;

    // If there's no table view in the store, return a set of default props so
    // we don't crash the app.
    if (!tableQuery) {
        logUsage('GetMailListTableProps_MissingTableView', mailboxInfo, { tableViewId });

        return {
            tableViewId,
            copilotPrioritizeOnDeliveryOn: false,
            supportsPinning: false,
            supportsFlagging: false,
            showCirclePersonas: false,
            showPreviewText: false,
            numMessagePreviewLines: 0,
            isSearchNotInDraftOrSentItemFolder: false,
            isSingleLine: false,
            highlightTerms: [],
            listViewType: 1,
            showCondensedPinnedRows: false,
            supportsHoverIcons: false,
            supportsDoubleClick: false,
            showFolderTag: false,
            supportsSnooze: false,
            canDragFromTable: false,
            isDraggable: false,
            supportsMove: false,
        };
    }

    const isDumpsterOrDumpsterSearchTable =
        isDumpsterTable(tableQuery) || isDumpsterSearchTable(tableQuery);
    const tableSupportsPinning =
        shouldTableSortByRenewTime(tableQuery) &&
        isCapabilityEnabled(pinItemCapability, mailboxInfo);
    const tableSupportsSnooze = !isGroupTableQuery(tableQuery) && isMailboxModifiable;
    const staticSearchScope = getMailSearchStore().staticSearchScope;

    // Show condensed pinned rows only if table supports pinning and is of type Folder
    // as Search table also supports pinning. Exclude Priority View, which is a Folder
    // table but should not show condensed pinned rows (same as search).
    const tableSupportsCondensedPinnedRows =
        tableView.tableQuery.type === 0 &&
        !isPriorityView(tableView.tableQuery as MailFolderTableQuery) &&
        !isNeedsReplyView(tableView.tableQuery as MailFolderTableQuery) &&
        !isCopilotResultsTable(tableQuery) &&
        tableSupportsPinning;

    // The ability to show preview text in list view can be disabled by OWA policy setting
    const showPreviewTextDisabled =
        getUserConfiguration().PolicySettings?.MessagePreviewsDisabled == true;

    // As we do not support moving all items in the folder, we disable dragging
    // in select all mode. We also disable dragging from Group table as that is not supported.
    const canDragFromTable =
        (tableQuery.folderId &&
            folderIdToName(tableQuery.folderId) !== 'notes' &&
            !tableView.isInVirtualSelectAllMode &&
            tableView.tableQuery.type == 0 &&
            (isMailboxModifiable ||
                isAccountFeatureEnabled('acct-pstFileImportExport', mailboxInfo) ||
                isUnifiedInbox)) ||
        tableView?.tableQuery.type == 2 ||
        tableView?.tableQuery.type == 3 ||
        tableView.tableQuery.type == 1;

    const searchScopeFolderName = (staticSearchScope as PrimaryMailboxSearchScope)?.folderId
        ? folderIdToName(
              (staticSearchScope as PrimaryMailboxSearchScope).folderId,
              true /* shouldSearchShared */
          )
        : undefined;

    const isSearchNotInDraftOrSentItemFolder =
        tableView?.tableQuery?.type == 1 &&
        searchScopeFolderName != 'sentitems' &&
        searchScopeFolderName != 'drafts';

    const folderName = tableQuery.folderId ? folderIdToName(tableQuery.folderId) : undefined;
    const supportsMove = folderName
        ? !isDumpsterOrDumpsterSearchTable &&
          folderName != 'notes' &&
          folderName != 'drafts' &&
          folderName != 'outbox' &&
          (isMailboxModifiable ||
              (isUnifiedInbox && isGlobalFeatureEnabled('fp-movetofolder-dialog')))
        : false;

    const supportsHoverIcons =
        !isDumpsterOrDumpsterSearchTable && (isMailboxModifiable || isUnifiedInbox);
    const isSingleLine = isSingleLineListView();
    const numMessagePreviewLines = getShowPreviewLinesInListView();
    const copilotPrioritizeOnDeliveryOn = isUnifiedInbox
        ? undefined
        : isPrioritizeOnDeliveryOn(mailboxInfo);

    return {
        tableViewId,
        copilotPrioritizeOnDeliveryOn,
        supportsPinning: tableSupportsPinning,
        supportsFlagging: shouldTableSupportFlag(tableQuery),
        showCirclePersonas: shouldTableShowCirclePersonas(tableQuery),
        showPreviewText:
            !showPreviewTextDisabled &&
            (isGlobalFeatureEnabled('tri-mlMultiLinePreview-dev')
                ? isSingleLine || numMessagePreviewLines > 0
                : getUserConfiguration()?.UserOptions?.ShowPreviewTextInListView ?? false),
        numMessagePreviewLines,
        isSearchNotInDraftOrSentItemFolder,
        isSingleLine,
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (95,9): Type 'string[] | undefined' is not assignable to type 'string[]'.
        // @ts-expect-error
        highlightTerms: tableView.highlightTerms,
        listViewType: tableQuery.listViewType,
        showCondensedPinnedRows: tableSupportsCondensedPinnedRows,
        supportsHoverIcons,
        supportsDoubleClick: !isDumpsterOrDumpsterSearchTable,
        showFolderTag: shouldShowFolderTag(tableQuery),
        supportsSnooze: tableSupportsSnooze,
        canDragFromTable,
        isDraggable: true,
        supportsMove,
    };
}
